"use client";

import { useEffect, useSyncExternalStore } from "react";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import { useCustomerCart } from "@/features/shop/customer/customer-shop.queries";
import { isPopulatedCartProduct } from "@/features/shop/types/customer-shop";
import {
  getLocalCustomerCart,
  subscribeLocalCustomerCart,
} from "./customer-cart-storage";
import { useCartStore } from "./cart.store";

/**
 * Mounted once (in AppLayout) to keep the global cart store in sync with
 * whichever cart is authoritative right now: the guest/non-customer
 * localStorage cart, or the signed-in customer's server cart. Mirrors the
 * local-cart-takes-precedence rule used at checkout (shop/cart page).
 */
export function useCustomerCartSync() {
  const { userAccount, loading } = useAuth();
  const isCustomer = !loading && userAccount?.role === "customer";

  const localCart = useSyncExternalStore(
    subscribeLocalCustomerCart,
    getLocalCustomerCart,
    () => null,
  );
  const hasLocalCart = Boolean(localCart?.items.length);

  const { data: serverCartResponse } = useCustomerCart({
    enabled: isCustomer && !hasLocalCart,
  });

  const setSnapshot = useCartStore((state) => state.setSnapshot);

  useEffect(() => {
    if (hasLocalCart && localCart) {
      const quantities: Record<string, number> = {};
      localCart.items.forEach((item) => {
        quantities[item.productId] = item.quantity;
      });
      setSnapshot({ shopId: localCart.shopId, source: "local", quantities });
      return;
    }

    if (isCustomer && serverCartResponse?.data) {
      const cart = serverCartResponse.data;
      const quantities: Record<string, number> = {};
      cart.items.forEach((item) => {
        const id = isPopulatedCartProduct(item.productId)
          ? item.productId._id
          : item.productId;
        if (id) quantities[String(id)] = item.quantity;
      });
      const shopId =
        typeof cart.shopId === "object" && cart.shopId && "_id" in cart.shopId
          ? cart.shopId._id
          : typeof cart.shopId === "string"
            ? cart.shopId
            : null;
      setSnapshot({ shopId, source: "server", quantities });
      return;
    }

    setSnapshot({ shopId: null, source: "none", quantities: {} });
  }, [hasLocalCart, localCart, isCustomer, serverCartResponse, setSnapshot]);
}
